import React from 'react';
import { TypeDefaultsPreview } from '@components/ui/cards/TypeDefaultsPreview';
import { Chip } from '@jotpsych/ui';
import { alpha, Box, Button, Stack, Typography } from '@mui/material';

import type {
  CleanupTypeVersion,
  DivergentGroup,
  GroupResolution,
  RenameEntry,
  SharedCatalogEntry,
  WizardData,
} from '../types';
import { personalKey } from '../utils/deriveWizardData';

import { RenameDigest } from './RenameDigest';
import { SharedCatalogPreview } from './SharedCatalogPreview';

interface SetMerge {
  keepName: string;
  mergedNames: string[];
  templateTitle: string | null;
}

export interface Step3ConfirmProps {
  data: WizardData;
  resolutions: Readonly<Record<string, GroupResolution>>;
  restoredCount: number;
  /** Quick-wins step output: identical-cluster consolidations. */
  quickWinMerges?: SetMerge[];
  /** Loose-ends step output: interchangeable-type merges to apply. */
  looseEndMerges?: SetMerge[];
  /**
   * Net effect of quick wins on the shared count (survivors promoted in
   * minus shared rows merged away) — quick-win rows are already filtered
   * out of `data`'s lanes but not out of the snapshot's automatic counts.
   */
  sharedAdjustment?: number;
  /** Stale-personal archive tool output (Archived step). */
  staleArchived?: { count: number; weeks: number };
  /** Additional archives from other surfaces (e.g. set-aside rows). */
  extraArchivedCount?: number;
  /** Stale interchangeable sets archived from quick wins / loose ends. */
  setArchives?: Array<{
    names: string[];
    templateTitle: string | null;
    count: number;
  }>;
  /** Per-clinician rename digest across all lanes. */
  renames?: RenameEntry[];
  /** The resulting shared catalog (picker preview) — the ground truth. */
  catalog?: SharedCatalogEntry[];
  /** Rows archived from the preview itself. */
  catalogArchivedIds?: ReadonlySet<string>;
  onToggleCatalogArchive?: (id: string) => void;
  /** Hide the inline Apply button when the wizard footer owns it. */
  showApply?: boolean;
  onApply: () => void;
  onBack?: () => void;
  applying?: boolean;
}

interface Outcome {
  sharedAfter: number;
  personalAfter: number;
  archivedAfter: number;
  winners: Array<{
    group: DivergentGroup;
    winner: CleanupTypeVersion;
    sharedName: string;
    /** True when this came from the personal-copies lane (pair promotion). */
    fromPersonalLane: boolean;
    /** Fold-in: the copy whose defaults the canonical adopts (if any). */
    adoptFrom: CleanupTypeVersion | null;
    /** Template outliers excluded from this merge (renamed separately). */
    setAsideCount: number;
  }>;
  archivedGroups: DivergentGroup[];
  keptSeparateGroups: number;
  deferredGroups: number;
  deferredNames: string[];
  freedTemplates: string[];
}

const computeOutcome = (
  data: WizardData,
  resolutions: Readonly<Record<string, GroupResolution>>,
  restoredCount: number
): Outcome => {
  const winners: Outcome['winners'] = [];
  const archivedGroups: DivergentGroup[] = [];
  const freed = new Set<string>();
  // Fold-in winners are the PRE-EXISTING org canonical (already counted in
  // the snapshot's automatic totals) — only genuinely new promotions add to
  // the shared count, and archived canonicals subtract from it.
  let newSharedWinners = 0;
  let sharedArchived = 0;
  // Provider-scoped divergent rows consumed by resolutions (promoted,
  // merged away, or archived). Org canonicals never sit in the personal
  // bucket, so they are excluded from the leftover math entirely.
  let divergentProviderConsumed = 0;
  let keptSeparateGroups = 0;
  let deferredGroups = 0;
  const deferredNames: string[] = [];
  let personalResolvedRows = 0;

  const resolveLane = (
    laneGroups: DivergentGroup[],
    keyOf: (g: DivergentGroup) => string,
    fromPersonalLane: boolean
  ): void => {
    for (const group of laneGroups) {
      const resolution = resolutions[keyOf(group)];
      if (resolution?.kind === 'make_shared') {
        const winner = group.versions.find(
          (v) => v.id === resolution.winnerId
        );
        if (winner) {
          const setAside = new Set(resolution.setAsideIds ?? []);
          const merging = group.versions.filter((v) => !setAside.has(v.id));
          winners.push({
            group,
            winner,
            sharedName: resolution.sharedName?.trim() || group.displayName,
            fromPersonalLane,
            adoptFrom:
              group.versions.find(
                (v) => v.id === resolution.adoptDefaultsFromId
              ) ?? null,
            setAsideCount: setAside.size,
          });
          if (winner.ownerScope !== 'organization') newSharedWinners += 1;
          // Set-aside outliers stay personal with their own templates.
          if (fromPersonalLane) {
            personalResolvedRows += merging.length;
          } else {
            divergentProviderConsumed += merging.filter(
              (v) => v.ownerScope !== 'organization'
            ).length;
          }
          for (const loser of merging) {
            if (
              loser.id !== winner.id &&
              loser.templateScope === 'user' &&
              loser.templateTitle &&
              loser.templateTitle !== winner.templateTitle
            ) {
              freed.add(loser.templateTitle);
            }
          }
        }
      } else if (resolution?.kind === 'archive_all') {
        archivedGroups.push(group);
        if (fromPersonalLane) {
          personalResolvedRows += group.versions.length;
        } else {
          divergentProviderConsumed += group.versions.filter(
            (v) => v.ownerScope !== 'organization'
          ).length;
          sharedArchived += group.versions.filter(
            (v) => v.ownerScope === 'organization'
          ).length;
        }
        for (const version of group.versions) {
          if (version.templateScope === 'user' && version.templateTitle) {
            freed.add(version.templateTitle);
          }
        }
      } else if (resolution?.kind === 'keep_separate') {
        // An explicit decision — types stay as they are, by choice. Not a
        // deferral: the admin already looked at this group.
        keptSeparateGroups += 1;
      } else {
        deferredGroups += 1;
        deferredNames.push(group.displayName);
      }
    }
  };

  resolveLane(data.divergentGroups, (g) => g.nameKey, false);
  resolveLane(data.personalClusters, (g) => personalKey(g.nameKey), true);

  // Only provider-scoped rows live in the personal bucket — a group's org
  // canonical is shared before AND after, whatever the admin decides
  // (including when the group is deferred).
  const divergentProviderRows = data.divergentGroups.reduce(
    (sum, g) =>
      sum + g.versions.filter((v) => v.ownerScope !== 'organization').length,
    0
  );
  const archivedRows = archivedGroups.reduce(
    (sum, g) => sum + g.versions.length,
    0
  );
  const sharedAfter =
    data.snapshot.autoPromoteCount +
    data.snapshot.alreadySharedCount +
    newSharedWinners -
    sharedArchived;
  const personalAfter =
    data.personalTypes.length -
    personalResolvedRows +
    (divergentProviderRows - divergentProviderConsumed);

  return {
    sharedAfter,
    personalAfter,
    archivedAfter: data.snapshot.archivedCount - restoredCount + archivedRows,
    winners,
    archivedGroups,
    keptSeparateGroups,
    deferredGroups,
    deferredNames,
    freedTemplates: [...freed],
  };
};

/**
 * Wizard Step 3 (GDN-2238): the payoff — before/after, the shared catalog
 * with each resolved type expandable to what it drives, freed templates
 * offered for archive, one confirm. Pure presentation.
 */
export const Step3Confirm: React.FC<Step3ConfirmProps> = ({
  data,
  resolutions,
  restoredCount,
  quickWinMerges = [],
  looseEndMerges = [],
  sharedAdjustment = 0,
  staleArchived = { count: 0, weeks: 12 },
  extraArchivedCount = 0,
  setArchives = [],
  renames = [],
  catalog,
  catalogArchivedIds = new Set<string>(),
  onToggleCatalogArchive,
  showApply = true,
  onApply,
  applying = false,
}) => {
  const outcome = computeOutcome(data, resolutions, restoredCount);
  const looseEndMergedAway = looseEndMerges.reduce(
    (sum, m) => sum + m.mergedNames.length,
    0
  );
  const quickWinMergedAway = quickWinMerges.reduce(
    (sum, m) => sum + m.mergedNames.length,
    0
  );
  const setArchivedCount = setArchives.reduce((sum, a) => sum + a.count, 0);
  // The catalog is the actual row set — when provided it IS the shared
  // count, immune to bookkeeping drift across the lanes.
  const sharedCount = catalog
    ? catalog.length - catalogArchivedIds.size
    : outcome.sharedAfter + sharedAdjustment - looseEndMergedAway;
  const archivedCount =
    outcome.archivedAfter +
    staleArchived.count +
    setArchivedCount +
    extraArchivedCount +
    catalogArchivedIds.size;

  // Two survivors can end a run sharing a name (a quick-win keeper and a
  // duplicate-group winner both landing on "Follow-up", say) — providers
  // can't tell them apart in the picker. Flag the collisions so the admin
  // renames or archives one before applying.
  const collidingNames = (() => {
    const firstSeen = new Map<string, string>();
    const collisions = new Map<string, string>();
    for (const entry of catalog ?? []) {
      if (catalogArchivedIds.has(entry.version.id)) continue;
      const key = entry.name.trim().toLowerCase();
      if (!key) continue;
      if (firstSeen.has(key)) collisions.set(key, firstSeen.get(key) as string);
      else firstSeen.set(key, entry.name.trim());
    }
    return [...collisions.values()];
  })();

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Your practice&apos;s shared appointment types
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 640 }}
      >
        Everyone schedules with these. Providers can still adjust any detail
        per visit; practice administrators manage the defaults here.
      </Typography>

      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          px: 2,
          py: 1.25,
          mb: 2,
          bgcolor: theme.palette.background.paper,
        })}
      >
        <Typography variant="body2">
          Before <b style={{ fontSize: 18 }}>{data.snapshot.totalTypes}</b>{' '}
          types, mixed owners
        </Typography>
        <Typography variant="h6" color="text.disabled">
          →
        </Typography>
        <Typography variant="body2" sx={{ color: 'success.dark' }}>
          After <b style={{ fontSize: 18 }}>{sharedCount}</b> shared types ·{' '}
          {archivedCount} archived ·{' '}
          {outcome.personalAfter} staying personal
          {outcome.keptSeparateGroups > 0 &&
            ` · ${outcome.keptSeparateGroups} kept separate`}
          {outcome.deferredGroups > 0 &&
            ` · ${outcome.deferredGroups} groups deferred`}
        </Typography>
      </Box>

      {collidingNames.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark' }}>
            ⚠️{' '}
            <b>
              {collidingNames.length === 1
                ? 'Two shared types share a name'
                : `${collidingNames.length} shared names are used twice`}
            </b>{' '}
            ({collidingNames.map((n) => `"${n}"`).join(' · ')}) — providers
            won&apos;t be able to tell them apart in the picker. Rename one on
            the Loose ends step, or archive the extra below.
          </Typography>
        </Box>
      )}

      {catalog && onToggleCatalogArchive && (
        <SharedCatalogPreview
          entries={catalog}
          archivedIds={catalogArchivedIds}
          onToggleArchive={onToggleCatalogArchive}
          disabled={applying}
        />
      )}

      {outcome.winners.length > 0 && (
        <Stack
          spacing={0.75}
          sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5, mb: 2 }}
        >
          {outcome.winners.map(({ group, winner, sharedName, adoptFrom, setAsideCount }) => (
            <Box
              key={group.nameKey}
              sx={(theme) => ({
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                bgcolor: theme.palette.background.paper,
              })}
            >
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sharedName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {winner.ownerScope === 'organization'
                    ? `${group.versions.length - 1 - setAsideCount} ${group.versions.length - setAsideCount === 2 ? 'copy' : 'copies'} folded into the practice-wide version`
                    : `merged from ${group.versions.length - setAsideCount} versions · ${
                        winner.provider
                          ? `${winner.provider}'s wins`
                          : 'unassigned wins'
                      }`}
                  {adoptFrom &&
                    ` · adopting ${adoptFrom.provider ?? 'a copy'}'s defaults`}
                  {setAsideCount > 0 &&
                    ` · ${setAsideCount} set aside for renaming`}
                  {sharedName !== group.displayName &&
                    ` · renamed from "${group.displayName}"`}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  {winner.templateScope === 'user' ? (
                    <Chip
                      size="xSmall"
                      variant="soft"
                      color="warning"
                      label="template is personal → will be promoted too"
                    />
                  ) : (
                    <Chip
                      size="xSmall"
                      variant="soft"
                      color="success"
                      label="shared"
                    />
                  )}
                </Box>
              </Stack>
              <Box sx={{ mt: 0.5 }}>
                <TypeDefaultsPreview
                  variant="inline"
                  defaults={(adoptFrom ?? winner).defaults}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {outcome.archivedGroups.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark' }}>
            🗂 <b>{outcome.archivedGroups.length} stale duplicate
            {outcome.archivedGroups.length === 1 ? ' group' : ' groups'}</b>{' '}
            archived instead of merged (
            {outcome.archivedGroups.reduce(
              (sum, g) => sum + g.versions.length,
              0
            )}{' '}
            types):{' '}
            {outcome.archivedGroups
              .slice(0, 3)
              .map((g) => g.displayName)
              .join(' · ')}
            {outcome.archivedGroups.length > 3 &&
              ` · +${outcome.archivedGroups.length - 3} more`}
            . They stay on past notes and can be restored anytime.
          </Typography>
        </Box>
      )}

      {quickWinMerges.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" sx={{ color: 'success.dark' }}>
            ⚡ <b>{quickWinMergedAway} types</b> consolidated as quick wins
            (providers already agreed — identical setups):{' '}
            {quickWinMerges
              .slice(0, 3)
              .map((m) => `${m.mergedNames.slice(0, 2).join(' + ')}${m.mergedNames.length > 2 ? ' + …' : ''} → "${m.keepName}"`)
              .join(' · ')}
            {quickWinMerges.length > 3 &&
              ` · +${quickWinMerges.length - 3} more sets`}
          </Typography>
        </Box>
      )}

      {setArchives.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark' }}>
            🗂 <b>{setArchivedCount} types</b> archived instead of merged —
            whole sets nobody uses anymore:{' '}
            {setArchives
              .slice(0, 3)
              .map((a) => {
                // Replication sets repeat the same name — collapse "X + X".
                const uniq = [...new Set(a.names)];
                return (
                  uniq.slice(0, 2).join(' + ') + (uniq.length > 2 ? ' + …' : '')
                );
              })
              .join(' · ')}
            {setArchives.length > 3 && ` · +${setArchives.length - 3} more sets`}
            . Reversible; past notes untouched.
          </Typography>
        </Box>
      )}

      {staleArchived.count > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark' }}>
            🗂 <b>{staleArchived.count} stale personal types</b> archived
            (unused ≥ {staleArchived.weeks} weeks, no upcoming appointments)
            — reversible, past notes untouched.
          </Typography>
        </Box>
      )}

      {looseEndMerges.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" sx={{ color: 'success.dark' }}>
            🔗 <b>{looseEndMergedAway} interchangeable types</b> merged in
            loose ends (same template &amp; defaults, name only):{' '}
            {looseEndMerges
              .slice(0, 3)
              .map((m) => `${m.mergedNames.join(' + ')} → "${m.keepName}"`)
              .join(' · ')}
            {looseEndMerges.length > 3 &&
              ` · +${looseEndMerges.length - 3} more sets`}
          </Typography>
        </Box>
      )}

      {outcome.freedTemplates.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          })}
        >
          <Typography variant="caption" sx={{ color: 'success.dark', flexGrow: 1 }}>
            🧹 <b>{outcome.freedTemplates.length} templates</b> will no longer
            be used by any type — we can archive them too:{' '}
            {outcome.freedTemplates.slice(0, 3).join(' · ')}
            {outcome.freedTemplates.length > 3 &&
              ` · +${outcome.freedTemplates.length - 3} more`}
          </Typography>
        </Box>
      )}

      {outcome.deferredNames.length > 0 && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 2,
          })}
        >
          <Typography variant="caption" color="text.secondary">
            ⏸ <b>{outcome.deferredNames.length} groups deferred</b> — nothing
            changes for them; re-run the cleanup anytime to pick them back
            up: {outcome.deferredNames.slice(0, 6).join(' · ')}
            {outcome.deferredNames.length > 6 &&
              ` · +${outcome.deferredNames.length - 6} more`}
          </Typography>
        </Box>
      )}

      <RenameDigest renames={renames} />

      {showApply && (
      <Button
        variant="contained"
        color="success"
        onClick={onApply}
        disabled={applying}
      >
        {applying ? 'Applying…' : 'Apply changes'}
      </Button>
      )}
      {showApply && (
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ display: 'block', mt: 1 }}
      >
        Reversible — every change is provenance-logged
      </Typography>
      )}
    </Box>
  );
};
